import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FloatingButtons from '../../components/feature/FloatingButtons';
import AgendamentoModal from '../../components/feature/AgendamentoModal';
import AnimatedSection from '../../components/base/AnimatedSection';

// ─── Dados ────────────────────────────────────────────────────────────────────

const PEQUENOS_PROCEDIMENTOS = [
  {
    icon: 'ri-scissors-cut-line',
    title: 'Remoção de Lesões Benignas',
    desc: 'Procedimento cirúrgico para remoção de pintas, verrugas, cistos, lipomas e outras lesões benignas da pele. Realizado com anestesia local, técnica minimamente invasiva e sutura delicada para cicatrizes discretas.',
    items: ['Pintas e nevos', 'Verrugas', 'Cistos sebáceos', 'Lipomas', 'Fibromas'],
    direction: 'left' as const,
    delay: 0,
  },
  {
    icon: 'ri-microscope-line',
    title: 'Biópsia de Pele',
    desc: 'Coleta de pequeno fragmento de pele para análise laboratorial e diagnóstico preciso de lesões suspeitas. Procedimento rápido, com anestesia local e mínimo desconforto.',
    items: ['Diagnóstico de lesões suspeitas', 'Investigação de manchas', 'Confirmação de doenças de pele', 'Análise histopatológica'],
    direction: 'right' as const,
    delay: 0,
  },
  {
    icon: 'ri-temp-cold-line',
    title: 'Criocirurgia',
    desc: 'Tratamento por congelamento com nitrogênio líquido para destruição de lesões superficiais da pele. Procedimento rápido, sem necessidade de anestesia na maioria dos casos.',
    items: ['Verrugas virais', 'Queratoses seborreicas', 'Queratoses actínicas', 'Molusco contagioso', 'Lesões benignas superficiais'],
    direction: 'left' as const,
    delay: 100,
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Eletrocoagulação',
    desc: 'Técnica que utiliza corrente elétrica de alta frequência para cauterização e remoção de pequenas lesões superficiais. Procedimento rápido, preciso e com ótima cicatrização.',
    items: ['Verrugas', 'Siringomas', 'Xantelasmas', 'Angiomas', 'Queratoses seborreicas', 'Acrocórdons (papilomas)'],
    direction: 'right' as const,
    delay: 100,
  },
];

const TECNOLOGIAS = [
  { icon: 'ri-syringe-line', title: 'Toxina Botulínica', desc: 'Tratamento para suavização de rugas de expressão e linhas finas. Aplicação precisa para resultados naturais, preservando a expressividade facial. Ideal para testa, glabela e região periocular.', delay: 0 },
  { icon: 'ri-heart-pulse-line', title: 'Bioestimulador de Colágeno', desc: 'Estimula a produção natural de colágeno, promovendo rejuvenescimento gradual e duradouro. Melhora a firmeza, textura e qualidade da pele. Resultados progressivos e naturais.', delay: 80 },
  { icon: 'ri-lightbulb-flash-line', title: 'Luz Intensa Pulsada (IPL)', desc: 'Tecnologia para tratamento de manchas, vasos, rosácea e rejuvenescimento. Melhora a textura da pele, uniformiza o tom e reduz sinais de fotoenvelhecimento.', delay: 160 },
  { icon: 'ri-contrast-drop-2-line', title: 'Laser Ablativo', desc: 'Tratamento para cicatrizes de acne, rugas profundas, manchas e rejuvenescimento intenso. Remove camadas superficiais da pele estimulando renovação celular.', delay: 240 },
  { icon: 'ri-radio-line', title: 'Radiofrequência Monopolar', desc: 'Tecnologia para flacidez facial e corporal. Aquece as camadas profundas da pele estimulando produção de colágeno e elastina. Promove firmeza, contorno e lifting não cirúrgico.', delay: 320 },
  { icon: 'ri-sound-module-line', title: 'Ultrassom Macro e Microfocado (HIFU)', desc: 'Lifting não cirúrgico com ultrassom de alta intensidade. Atua nas camadas profundas da pele promovendo contração imediata e estímulo de colágeno. Resultados progressivos e duradouros.', delay: 400 },
  { icon: 'ri-pulse-line', title: 'MMP Facial e Capilar', desc: 'Microagulhamento mecanizado para rejuvenescimento facial e tratamento capilar. Estimula produção de colágeno, melhora textura, cicatrizes e favorece crescimento capilar.', delay: 480 },
  { icon: 'ri-drop-line', title: 'Peelings', desc: 'Tratamentos químicos para renovação celular, clareamento de manchas, controle de oleosidade e melhora da textura. Diversos tipos e intensidades conforme necessidade individual.', delay: 560 },
];

const DERMATOLOGIA_CLINICA = [
  { icon: 'ri-drop-line', title: 'Acne', desc: 'Tratamento completo para acne em todas as fases e graus de severidade. Abordagem individualizada com medicações tópicas, orais e procedimentos complementares.', delay: 0 },
  { icon: 'ri-contrast-2-line', title: 'Rosácea', desc: 'Controle da vermelhidão facial, vasos dilatados e lesões inflamatórias. Tratamento com medicações específicas, laser e luz intensa pulsada.', delay: 80 },
  { icon: 'ri-file-damage-line', title: 'Psoríase', desc: 'Tratamento para controle das lesões descamativas e inflamação. Opções terapêuticas incluem medicações tópicas, fototerapia e medicações sistêmicas conforme gravidade.', delay: 160 },
  { icon: 'ri-heart-add-line', title: 'Dermatite Atópica', desc: 'Manejo da pele seca, coceira e lesões eczematosas. Tratamento com hidratantes específicos, medicações tópicas e sistêmicas quando necessário.', delay: 240 },
  { icon: 'ri-sun-line', title: 'Melasma', desc: 'Tratamento das manchas escuras no rosto causadas por sol, hormônios e predisposição genética. Combinação de clareadores tópicos, peelings, laser e fotoproteção rigorosa.', delay: 320 },
  { icon: 'ri-user-heart-line', title: 'Alopecia Androgenética', desc: 'Tratamento da queda de cabelo hereditária em homens e mulheres. Opções incluem medicações tópicas, orais, microagulhamento capilar e bioestimuladores.', delay: 400 },
  { icon: 'ri-leaf-line', title: 'Dermatite Seborreica', desc: 'Controle da descamação, oleosidade e vermelhidão no couro cabeludo e face. Tratamento com shampoos específicos, medicações tópicas e orientações para manutenção.', delay: 480 },
  { icon: 'ri-water-flash-line', title: 'Hiperidrose', desc: 'Tratamento do suor excessivo em axilas, mãos, pés e outras áreas. Opções incluem antitranspirantes específicos, toxina botulínica e medicações sistêmicas.', delay: 560 },
  { icon: 'ri-capsule-line', title: 'Hidradenite Supurativa', desc: 'Tratamento de nódulos e abscessos dolorosos em axilas, virilha e outras áreas. Abordagem multidisciplinar com medicações e procedimentos cirúrgicos quando necessário.', delay: 640 },
  { icon: 'ri-plant-line', title: 'Micoses', desc: 'Diagnóstico e tratamento de infecções fúngicas de pele, unhas e couro cabeludo. Medicações tópicas e orais conforme localização e extensão.', delay: 720 },
  { icon: 'ri-contrast-drop-line', title: 'Vitiligo', desc: 'Tratamento das manchas brancas na pele com perda de pigmentação. Opções incluem medicações tópicas, fototerapia e procedimentos específicos.', delay: 800 },
];

// ─── Componentes de Seção ─────────────────────────────────────────────────────

interface SectionHeaderProps {
  badge: string;
  title: string;
  description: string;
  bgWhite?: boolean;
}

function SectionHeader({ badge, title, description, bgWhite = false }: SectionHeaderProps) {
  return (
    <AnimatedSection>
      <div className="max-w-3xl 2xl:max-w-4xl mb-12 sm:mb-16">
        <div className={`inline-flex items-center gap-2 px-4 py-2 ${bgWhite ? 'bg-white' : 'bg-[#FBF8F3]'} rounded-full mb-6`}>
          <div className="w-2 h-2 bg-[#C4A57B] rounded-full" />
          <span className="text-sm text-[#8B7355] font-medium">{badge}</span>
        </div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl 2xl:text-6xl font-light text-[#4A3F35] mb-6 leading-tight">{title}</h2>
        <p className="text-base sm:text-lg text-[#8B7355] leading-relaxed">{description}</p>
      </div>
    </AnimatedSection>
  );
}

interface TreatmentCardProps {
  icon: string;
  title: string;
  desc: string;
  items?: string[];
  delay?: number;
  bgLight?: boolean;
}

function TreatmentCard({ icon, title, desc, items, delay = 0, bgLight = false }: TreatmentCardProps) {
  return (
    <AnimatedSection delay={delay}>
      <div className={`${bgLight ? 'bg-[#FBF8F3]' : 'bg-white'} rounded-2xl p-6 sm:p-8 border border-[#E8DCC8] h-full`}>
        <div className={`w-12 h-12 ${bgLight ? 'bg-white' : 'bg-[#FBF8F3]'} rounded-full flex items-center justify-center mb-6`}>
          <i className={`${icon} text-[#C4A57B] text-2xl`} />
        </div>
        <h3 className="text-xl font-medium text-[#4A3F35] mb-4">{title}</h3>
        <p className="text-[#8B7355] leading-relaxed text-sm mb-4">{desc}</p>
        {items && (
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item} className="flex items-center gap-2 text-sm text-[#8B7355]">
                <i className="ri-check-line text-[#C4A57B]" />
                {item}
              </li>
            ))}
          </ul>
        )}
      </div>
    </AnimatedSection>
  );
}

interface AgendarButtonProps {
  onClick: () => void;
  label?: string;
}

function AgendarButton({ onClick, label = 'Agendar Avaliação' }: AgendarButtonProps) {
  return (
    <AnimatedSection delay={200} className="text-center mt-12 sm:mt-16">
      <button
        onClick={onClick}
        className="inline-block px-10 py-4 bg-[#C4A57B] text-white text-sm font-medium rounded-full hover:bg-[#B39568] transition-colors whitespace-nowrap cursor-pointer"
      >
        {label}
      </button>
    </AnimatedSection>
  );
}

// ─── Página de Tratamentos ────────────────────────────────────────────────────

export default function TratamentosPage() {
  const [agendamentoOpen, setAgendamentoOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const openAgendar = () => setAgendamentoOpen(true);
  const closeAgendar = () => setAgendamentoOpen(false);

  return (
    <>
      <title>Tratamentos Dermatológicos em Criciúma SC | Dra. Laura Folchini</title>
      <meta name="description" content="HIFU, bioestimulador de colágeno, toxina botulínica, laser e dermatologia clínica. Procedimentos especializados em Criciúma e Tubarão/SC. Agende sua avaliação." />
      <link rel="canonical" href="https://dralaurafolchini.com.br/tratamentos" />
      <div className="min-h-screen bg-[#FBF8F3]">
        {agendamentoOpen && <AgendamentoModal onClose={closeAgendar} />}
        <Navbar onAgendar={openAgendar} />

        {/* Hero */}
        <section className="relative flex items-center pt-16 min-h-[50vh] sm:min-h-[60vh]">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/fdedde699cd3a3e591fd108cc73d3ec9.jpg"
              alt="Tratamentos dermatológicos especializados HIFU medicina estética Criciúma SC Dra. Laura Folchini"
              className="w-full h-full object-cover object-center"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C130B]/80 via-[#2C1810]/55 to-[#1C130B]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />

          <div className="relative z-10 w-full max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16 py-16 sm:py-24">
            {/* Breadcrumb / Voltar */}
            <div
              className="mb-8 opacity-0"
              style={{ animation: 'fadeInUp 0.5s ease-out 0.05s forwards' }}
            >
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm cursor-pointer group"
              >
                <i className="ri-arrow-left-line text-base group-hover:-translate-x-1 transition-transform" />
                Voltar para Home
              </Link>
            </div>

            <div className="max-w-3xl">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full mb-6 opacity-0"
                style={{ animation: 'fadeInUp 0.6s ease-out 0.15s forwards' }}
              >
                <div className="w-2 h-2 bg-[#C4A57B] rounded-full" />
                <span className="text-sm text-white/90 font-medium tracking-wide">Tratamentos · Dra. Laura Folchini</span>
              </div>
              <h1
                className="text-4xl sm:text-5xl md:text-7xl 2xl:text-8xl font-light text-white mb-6 leading-tight opacity-0"
                style={{ animation: 'fadeInUp 0.7s ease-out 0.28s forwards' }}
              >
                Procedimentos<br />
                <span className="italic text-[#E8CFA8]">especializados</span>
              </h1>
              <p
                className="text-base sm:text-lg text-white/80 max-w-xl leading-relaxed opacity-0"
                style={{ animation: 'fadeInUp 0.7s ease-out 0.42s forwards' }}
              >
                Cada tratamento é cuidadosamente indicado após avaliação individual, garantindo segurança, eficácia e resultados que respeitam a naturalidade de cada paciente.
              </p>
            </div>
          </div>
        </section>

        {/* Pequenos Procedimentos */}
        <section id="pequenos-procedimentos" className="py-16 sm:py-24 bg-white">
          <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16">
            <SectionHeader
              badge="Cirurgia Dermatológica"
              title="Pequenos procedimentos"
              description="Realizados com técnica precisa e mínima cicatriz, em ambiente ambulatorial seguro e confortável. Cada procedimento é executado com cuidado e atenção aos detalhes para garantir os melhores resultados."
            />
            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-6 sm:gap-8 2xl:gap-8">
              {PEQUENOS_PROCEDIMENTOS.map((item) => (
                <AnimatedSection key={item.title} direction={item.direction} delay={item.delay}>
                  <TreatmentCard
                    icon={item.icon}
                    title={item.title}
                    desc={item.desc}
                    items={item.items}
                    bgLight
                  />
                </AnimatedSection>
              ))}
            </div>
            <AgendarButton onClick={openAgendar} />
          </div>
        </section>

        {/* Tecnologias e Medicina Estética */}
        <section id="tecnologias-estetica" className="py-16 sm:py-24 bg-[#FBF8F3]">
          <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16">
            <SectionHeader
              badge="Estética Avançada"
              title="Tecnologias e medicina estética"
              description="Equipamentos modernos e técnicas atualizadas para os melhores resultados com máxima segurança e naturalidade. Procedimentos estéticos com tecnologia de ponta para rejuvenescimento, firmeza e harmonia facial."
              bgWhite
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6 sm:gap-8 2xl:gap-8">
              {TECNOLOGIAS.map((item) => (
                <TreatmentCard
                  key={item.title}
                  icon={item.icon}
                  title={item.title}
                  desc={item.desc}
                  delay={item.delay}
                />
              ))}
            </div>
            <AgendarButton onClick={openAgendar} />
          </div>
        </section>

        {/* Dermatologia Clínica */}
        <section id="dermatologia-clinica" className="py-16 sm:py-24 bg-white">
          <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16">
            <SectionHeader
              badge="Saúde da Pele"
              title="Dermatologia clínica"
              description="Diagnóstico e tratamento completo das principais condições dermatológicas com abordagem humanizada. Cada paciente recebe um plano terapêutico individualizado, considerando suas necessidades e características específicas."
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6 sm:gap-8 2xl:gap-8">
              {DERMATOLOGIA_CLINICA.map((item) => (
                <TreatmentCard
                  key={item.title}
                  icon={item.icon}
                  title={item.title}
                  desc={item.desc}
                  delay={item.delay}
                  bgLight
                />
              ))}
            </div>
            <AgendarButton onClick={openAgendar} label="Agendar Consulta" />
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-16 sm:py-24 bg-[#8B7355]">
          <div className="max-w-4xl 2xl:max-w-5xl mx-auto px-4 sm:px-8 2xl:px-16 text-center">
            <AnimatedSection>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
                <div className="w-2 h-2 bg-white rounded-full" />
                <span className="text-sm text-white font-medium">Agende Sua Consulta</span>
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl 2xl:text-6xl font-light text-white mb-6 leading-tight">
                Pronta para cuidar de você
              </h2>
              <p className="text-base sm:text-lg text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">
                Cada tratamento é cuidadosamente indicado após avaliação individual. Agende sua consulta e receba um atendimento personalizado, humanizado e focado no seu bem-estar.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={openAgendar}
                  className="px-10 py-4 bg-white text-[#8B7355] text-sm font-medium rounded-full hover:bg-[#FBF8F3] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Agendar Consulta
                </button>
                <Link
                  to="/"
                  className="px-10 py-4 bg-transparent text-white text-sm font-medium rounded-full border-2 border-white hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  Voltar para Home
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </section>

        <Footer />
        <FloatingButtons onAgendar={openAgendar} />
      </div>
    </>
  );
}
